import React, { createContext, useContext, useReducer, useMemo } from "react";
import { TALENTION_INITIAL, TALENTION_HELPERS } from "./data.js";

/* ============================================================
   Talention — Store (Context + reducer)
   Exposes window.useStore, window.StoreProvider
   ============================================================ */

const StoreCtx = createContext(null);

function reducer(state, action) {
  switch (action.type) {
    /* --- Entity --- */
    case "UPDATE_ENTITY":
      return { ...state, entity: { ...state.entity, ...action.patch } };

    /* --- Departments --- */
    case "ADD_DEPT": {
      const id = Math.max(0, ...state.departments.map(d => d.id)) + 1;
      return { ...state, departments: [...state.departments, { id, ...action.dept, createdAt: nowStr() }] };
    }
    case "UPDATE_DEPT":
      return { ...state, departments: state.departments.map(d => d.id === action.id ? { ...d, ...action.patch } : d) };
    case "DELETE_DEPT":
      return { ...state, departments: state.departments.filter(d => d.id !== action.id) };

    /* --- Levels --- */
    case "ADD_LEVEL": {
      const id = Math.max(0, ...state.levels.map(d => d.id)) + 1;
      return { ...state, levels: [...state.levels, { id, ...action.lvl }] };
    }
    case "UPDATE_LEVEL":
      return { ...state, levels: state.levels.map(d => d.id === action.id ? { ...d, ...action.patch } : d) };
    case "DELETE_LEVEL":
      return { ...state, levels: state.levels.filter(d => d.id !== action.id) };

    /* --- Positions --- */
    case "ADD_POSITION": {
      const id = Math.max(0, ...state.positions.map(p => p.id)) + 1;
      const code = "P" + String(id).padStart(2, "0");
      return { ...state, positions: [...state.positions, { id, code, ...action.pos, points: 0, status: "PENDIENTE", createdAt: nowStr() }] };
    }
    case "UPDATE_POSITION":
      return { ...state, positions: state.positions.map(p => p.id === action.id ? { ...p, ...action.patch } : p) };
    case "DELETE_POSITION":
      return { ...state, positions: state.positions.filter(p => p.id !== action.id) };

    /* --- Factors --- */
    case "SET_FACTOR_SVPT": {
      const groups = state.factors.groups.map(g => g.id === action.groupId ? { ...g, svpt: action.value } : g);
      return { ...state, factors: { ...state.factors, groups } };
    }
    case "AUTOBALANCE_FACTORS": {
      const total = state.factors.groups.reduce((s, g) => s + g.svpt, 0);
      if (total === 100) return state;
      const groups = state.factors.groups.map(g => ({ ...g, svpt: Math.round((g.svpt / total) * 100) }));
      const diff = 100 - groups.reduce((s, g) => s + g.svpt, 0);
      if (diff !== 0) groups[0].svpt += diff;
      return { ...state, factors: { ...state.factors, groups } };
    }
    case "RESET_FACTORS": {
      const groups = state.factors.groups.map(g => ({ ...g, svpt: g.defaultSVPT }));
      return { ...state, factors: { ...state.factors, groups } };
    }
    case "TOGGLE_FACTOR_ITEM": {
      const items = { ...state.factors.items };
      items[action.groupId] = items[action.groupId].map(it =>
        it.id === action.itemId ? { ...it, selected: !it.selected } : it
      );
      return { ...state, factors: { ...state.factors, items } };
    }
    case "SET_ITEM_WEIGHT": {
      const items = { ...state.factors.items };
      items[action.groupId] = items[action.groupId].map(it =>
        it.id === action.itemId ? { ...it, weight: action.value } : it
      );
      return { ...state, factors: { ...state.factors, items } };
    }
    case "EQUALIZE_ITEM_WEIGHTS": {
      const items = { ...state.factors.items };
      const eligible = items[action.groupId].filter(it => it.selected && it.levels.length > 0);
      const eq = eligible.length > 0 ? Math.round(100 / eligible.length) : 0;
      items[action.groupId] = items[action.groupId].map(it =>
        it.selected && it.levels.length > 0 ? { ...it, weight: eq } : it
      );
      return { ...state, factors: { ...state.factors, items } };
    }

    /* --- Scales --- */
    case "UPDATE_SCALE":
      return { ...state, scales: state.scales.map(s => s.id === action.id ? { ...s, ...action.patch } : s) };
    case "ADD_SCALE": {
      const id = Math.max(0, ...state.scales.map(s => s.id)) + 1;
      return { ...state, scales: [...state.scales, { id, ...action.scale }] };
    }
    case "DELETE_SCALE":
      return { ...state, scales: state.scales.filter(s => s.id !== action.id) };

    /* --- Valuations --- */
    case "SET_VALUATION": {
      const cur = state.valuations[action.positionId] || {};
      const valuations = { ...state.valuations, [action.positionId]: { ...cur, [action.itemId]: action.levelId } };
      return { ...state, valuations };
    }
    case "CLEAR_VALUATION": {
      const valuations = { ...state.valuations };
      delete valuations[action.positionId];
      return { ...state, valuations };
    }
    case "FINALIZE_VALUATION": {
      return { ...state, positions: state.positions.map(p =>
        p.id === action.positionId ? { ...p, status: "FINALIZADA", points: action.points } : p) };
    }

    /* --- Users --- */
    case "ADD_USER": {
      const id = Math.max(0, ...state.users.map(u => u.id)) + 1;
      return { ...state, users: [...state.users, { id, ...action.user, createdAt: nowStr().slice(0, 10), lastLogin: "—" }] };
    }
    case "UPDATE_USER":
      return { ...state, users: state.users.map(u => u.id === action.id ? { ...u, ...action.patch } : u) };
    case "DELETE_USER":
      return { ...state, users: state.users.filter(u => u.id !== action.id) };

    /* --- Roles --- */
    case "ADD_ROLE": {
      const id = Math.max(0, ...state.roles.map(r => r.id)) + 1;
      return { ...state, roles: [...state.roles, { id, ...action.role }] };
    }
    case "UPDATE_ROLE":
      return { ...state, roles: state.roles.map(r => r.id === action.id ? { ...r, ...action.patch } : r) };
    case "DELETE_ROLE":
      return { ...state, roles: state.roles.filter(r => r.id !== action.id) };

    default:
      return state;
  }
}

function nowStr() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, TALENTION_INITIAL);

  // recompute position points whenever factors or valuations change
  const computedPositions = useMemo(() => {
    return state.positions.map(p => {
      const v = state.valuations[p.id];
      if (!v) return p;
      const pts = TALENTION_HELPERS.computePositionPoints(v, state.factors);
      const completed = TALENTION_HELPERS.countCompletedItems(v, state.factors);
      const required = completed.total;
      const filled = completed.done;
      let status = "PENDIENTE";
      if (filled === required && required > 0) status = "FINALIZADA";
      else if (filled > 0) status = "EN PROGRESO";
      return { ...p, points: pts, status };
    });
  }, [state.positions, state.valuations, state.factors]);

  // Build merged state for consumers
  const value = useMemo(() => ({
    state: { ...state, positions: computedPositions },
    dispatch,
    helpers: TALENTION_HELPERS,
  }), [state, computedPositions]);

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

function useStore() {
  return useContext(StoreCtx);
}

export { useStore, StoreProvider };